import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { AlertTriangle, AlertCircle, Info, Bell } from "lucide-react";

const SEVERITY_META = {
  critical: { Icon: AlertCircle, color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200", label: "Kritisch" },
  warn: { Icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", label: "Hinweis" },
  info: { Icon: Info, color: "text-sky-700", bg: "bg-sky-50", border: "border-sky-200", label: "Info" },
};

const TYPE_LABEL = {
  near_end_of_life: "Restnutzungsdauer < 12M",
  early_depreciation: "Vorzeitig abgeschrieben",
  fully_depreciated: "Voll abgeschrieben",
};

export function AssetWarningsWidget({ companyId, thresholdMonths = 12 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await apiClient.get(`/companies/${companyId}/assets/warnings`, {
          params: { threshold_months: thresholdMonths },
        });
        if (!cancel) setData(r.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("AssetWarningsWidget fetch failed:", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [companyId, thresholdMonths]);

  if (loading) {
    return (
      <Card className="p-4 border-slate-200">
        <div className="text-xs text-slate-500">Lade Anlage-Hinweise…</div>
      </Card>
    );
  }
  if (!data) return null;

  const counts = {
    critical: data.warnings.filter((w) => w.severity === "critical").length,
    warn: data.warnings.filter((w) => w.severity === "warn").length,
    info: data.warnings.filter((w) => w.severity === "info").length,
  };

  return (
    <Card className="p-5 border border-slate-200 bg-white" data-testid="asset-warnings-widget">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Bell className="h-4 w-4 text-amber-700" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Anlage-Hinweise</h3>
            <p className="text-[11px] text-slate-500">
              {data.warnings.length === 0
                ? `${data.total_assets} Anlagen geprüft — keine Hinweise`
                : `${data.warnings.length} Hinweis(e) für Treuhänder-Aufmerksamkeit`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px]">
          {counts.critical > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200" data-testid="asset-warnings-critical-count">
              {counts.critical} kritisch
            </span>
          )}
          {counts.warn > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid="asset-warnings-warn-count">
              {counts.warn} Hinweis
            </span>
          )}
          {counts.info > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200" data-testid="asset-warnings-info-count">
              {counts.info} Info
            </span>
          )}
        </div>
      </div>

      {data.warnings.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-4 text-center" data-testid="asset-warnings-empty">
          Alles im grünen Bereich — keine Anlage benötigt aktuell Aufmerksamkeit.
        </div>
      ) : (
        <ul className="space-y-2">
          {data.warnings.slice(0, 8).map((w) => {
            const meta = SEVERITY_META[w.severity] || SEVERITY_META.info;
            const Icon = meta.Icon;
            return (
              <li
                key={w.asset_id + w.warning_type}
                className={`flex items-start gap-2.5 rounded-lg border ${meta.border} ${meta.bg} px-3 py-2`}
                data-testid={`asset-warning-${w.severity}-${w.asset_id}`}
              >
                <Icon className={`h-4 w-4 ${meta.color} shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm font-medium text-slate-800 truncate">{w.asset_name}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 shrink-0">
                      {TYPE_LABEL[w.warning_type] || w.warning_type}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">{w.message}</div>
                  <div className="flex gap-3 text-[10px] text-slate-500 mt-1">
                    <span>BV: <span className="font-mono">{w.book_value.toFixed(2)} {data.currency}</span></span>
                    {w.months_remaining !== null && w.months_remaining !== undefined && (
                      <span>Rest: <span className="font-mono">{w.months_remaining} Mt.</span></span>
                    )}
                    <span>Fortschritt: <span className="font-mono">{w.depreciation_progress_pct.toFixed(1)}%</span></span>
                  </div>
                </div>
              </li>
            );
          })}
          {data.warnings.length > 8 && (
            <li className="text-[11px] text-slate-500 italic text-center pt-1">
              … und {data.warnings.length - 8} weitere Hinweise.
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}
